import React from "react";
import NotebookItem from "./NotebookItem";
import Viewing from "./Viewing";
import Editing from "./Editing";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { v4 as uuidv4 } from "uuid";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import useNotebookStore from "../../store/useNotebookStore";
import moment from "moment";

const NotebookList = ({
  ref3,
  showModal,
  fetchNotebooks,
}) => {
  const {
    notebookList,
    viewingNotebook,
    setViewingNotebook,
    editingNotebook,
    setEditingNotebook,
    cardTitle,
    cards,
    setCards,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setCardTitle,
    isEditing,
  } = useNotebookStore();

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  const validateCards = () => {
    if (!cardTitle.trim()) {
      alert("제목을 입력해주세요.");
      return false;
    }

    const incompleteCards = cards.filter(
      (card) =>
        (card.content || card.kor || card.img) &&
        !(card.content && card.kor && card.img)
    );

    if (incompleteCards.length > 0) {
      alert("이미지, 단어, 뜻 입력은 필수입니다.");
      return false;
    }

    const validCards = cards.filter(
      (card) => card.img && card.content && card.kor
    );

    if (validCards.length < 2) {
      alert("카드는 최소 2개 이상이어야 저장할 수 있습니다.");
      return false;
    }

    return validCards;
  };

  const handleUnsavedChanges = (callback) => {
    if (hasUnsavedChanges) {
      showModal(
        "작성하신 내용이 저장되지 않았어요. 바로 저장하시겠어요?",
        () => {
          handleSaveCards(notebookList[editingNotebook].id);
          setHasUnsavedChanges(false);
          callback();
        },
        () => {
          fetchNotebooks();
          setHasUnsavedChanges(false);
          callback();
        }
      );
    } else {
      callback();
    }
  };

  const handleSaveCards = async (notebookId) => {
    const validCards = validateCards();
    if (!validCards) return;

    const notebooksCollection = collection(db, "users", userId, "customCards");
    const querySnapshot = await getDocs(notebooksCollection);

    const notebookToUpdate = querySnapshot.docs.find(
      (doc) => doc.data().id === notebookId
    );

    if (notebookToUpdate) {
      await updateNotebook(notebookToUpdate, validCards);
    } else {
      await createNewNotebook(validCards);
    }

    fetchNotebooks();
    setEditingNotebook(null);
  };

  const handleNotebookView = (index) => {
    if (isEditing && hasUnsavedChanges) {
      alert("저장을 완료해주세요.");
      return;
    }

    handleUnsavedChanges(() => {
      if (viewingNotebook === index) {
        setViewingNotebook(null);
      } else {
        setViewingNotebook(index);
        setEditingNotebook(null);
        const selectedNotebook = notebookList[index];
        if (selectedNotebook.cards) {
          setCards(selectedNotebook.cards);
        }
      }
    });
  };

  const handleNotebookEdit = (index) => {
    if (isEditing && hasUnsavedChanges) {
      alert("저장을 완료해주세요.");
      return;
    }

    handleUnsavedChanges(() => {
      setEditingNotebook(index);
      setViewingNotebook(null);
      setCardTitle(notebookList[index].title);
      const selectedNotebook = notebookList[index];
      if (selectedNotebook.cards) {
        setCards(selectedNotebook.cards);
      }
    });
  };

  const handleMoveToCardList = async (notebook) => {
    showModal("단어장 카드 리스트로 이동할까요?", async () => {
      const cards = notebook.cards || [];
      if (cards.length > 0) {
        try {
          const cardGamesRef = collection(db, "users", userId, "cardGames");
          const q = query(cardGamesRef, where("id", "==", notebook.id));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            querySnapshot.forEach((doc) => {
              navigate(`/card-game/${doc.id}`);
            });
          }
        } catch (error) {
          console.error("파이어베이스 쿼리 오류:", error);
        }
      } else {
        console.error("카드 정보가 없습니다.");
      }
    });
  };

  const updateNotebook = async (notebookToUpdate, validCards) => {
    const notebookRef = doc(
      db,
      "users",
      userId,
      "customCards",
      notebookToUpdate.id
    );

    await updateDoc(notebookRef, {
      title: cardTitle,
      date: moment().format("YYYY-MM-DD HH:mm:ss"),
    });

    const cardsCollectionRef = collection(notebookRef, "cards");
    const existingCardsSnapshot = await getDocs(cardsCollectionRef);

    for (const cardDoc of existingCardsSnapshot.docs) {
      await deleteDoc(cardDoc.ref);
    }

    for (const card of validCards) {
      await addDoc(cardsCollectionRef, card);
    }

    await updateCardGame(notebookToUpdate.data().id, validCards);
  };

  const createNewNotebook = async (validCards) => {
    const newNotebookId = uuidv4();
    const newNotebookRef = await addDoc(
      collection(db, "users", userId, "customCards"),
      {
        id: newNotebookId,
        date: moment().format("YYYY-MM-DD HH:mm:ss"),
        title: cardTitle,
      }
    );

    const cardsCollectionRef = collection(newNotebookRef, "cards");
    for (const card of validCards) {
      await addDoc(cardsCollectionRef, card);
    }

    const newGameRef = await addDoc(
      collection(db, "users", userId, "cardGames"),
      {
        id: newNotebookId,
        customWords: validCards,
      }
    );

    showModal("새로운 수첩을 저장했어요. 확인 해볼까요?", () => {
      navigate(`/card-game/${newGameRef.id}`);
    });

    return newNotebookId;
  };

  const updateCardGame = async (notebookId, validCards) => {
    const cardGamesSnapshot = await getDocs(
      collection(db, "users", userId, "cardGames")
    );

    const gameDoc = cardGamesSnapshot.docs.find(
      (doc) => doc.data().id === notebookId
    );

    if (gameDoc) {
      const gameRef = doc(db, "users", userId, "cardGames", gameDoc.id);
      console.log("gameDoc.id", gameDoc.id);

      await updateDoc(gameRef, {
        customWords: validCards,
      });

      showModal("수첩을 업데이트했어요. 확인 해볼까요?", () => {
        navigate(`/card-game/${gameDoc.id}`);
      });
    } else {
      console.error(
        "해당 ID를 가진 cardGames 문서를 찾을 수 없습니다. ID: " + notebookId
      );
    }
  };

  const handleDeleteNotebook = async (notebookId) => {
    const querySnapshot = await getDocs(
      collection(db, "users", userId, "customCards")
    );
    const notebookToDelete = querySnapshot.docs.find(
      (doc) => doc.data().id === notebookId
    );

    if (notebookToDelete) {
      showModal("수첩을 삭제할까요?", async () => {
        const cardsCollectionRef = collection(notebookToDelete.ref, "cards");
        const cardsSnapshot = await getDocs(cardsCollectionRef);

        for (const cardDoc of cardsSnapshot.docs) {
          await deleteDoc(cardDoc.ref);
        }

        await deleteDoc(notebookToDelete.ref);

        const cardGamesRef = collection(db, "users", userId, "cardGames");
        const q = query(cardGamesRef, where("id", "==", notebookId));
        const cardGamesSnapshot = await getDocs(q);

        for (const gameDoc of cardGamesSnapshot.docs) {
          await deleteDoc(gameDoc.ref);
        }

        setEditingNotebook(false); // isEditing을 false로 수정
        fetchNotebooks();
      });
    } else {
      console.error("수첩 삭제 오류");
    }
  };
  
  if (!notebookList?.length) {
    return (
      <div className="notebook-list">
        <div className="notebook-info">
          <p>저장된 수첩이 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {notebookList.map((notebook, index) => (
        <NotebookItem
          key={index}
          notebook={notebook}
          index={index}
          handleNotebookView={handleNotebookView}
          handleNotebookEdit={handleNotebookEdit}
          handleDeleteNotebook={handleDeleteNotebook}
          handleMoveToCardList={handleMoveToCardList}
        >
          {viewingNotebook === index && <Viewing notebookId={notebook.id} />}
          {editingNotebook === index && (
            <Editing
              refs={{ ref3 }}
              notebookId={notebook.id}
              showModal={showModal}
              handleSaveCards={handleSaveCards}
            />
          )}
        </NotebookItem>
      ))}
    </div>
  );
};

export default NotebookList;
